/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useRef } from 'react';
import { ModelManager } from '@adobe/aem-spa-page-model-manager';
import path from 'path';
import { defineComponentMapping } from '../import-components';
import {
  canUseDOM,
  CustomModelClient,
  getEnvProp,
  populatePIdataLayer,
  respGridUtil,
  UserSessionProvider,
} from '@marriott/mi-headless-utils';
import '../styles/styles.css';
import {
  useDCAPageModelStore,
  useClientEnvVarsStore,
  PageModelStoreType,
  PageModelState,
  createPageModelStore,
  PageModelContext,
} from '@marriott/mi-store-utils';
import { version } from '@marriott/global-styles/package.json';
import { setSubDirectoryPrefix, ACDLanalyticsWrapper } from '@marriott/mi-luxury-components';

// TODO: remove this after clientlibs issue fixed
import '@glidejs/glide/src/assets/sass/glide.core.scss';
import '@glidejs/glide/src/assets/sass/glide.theme.scss';
import rtlPlugin from 'stylis-plugin-rtl';
import { StyleSheetManager } from 'styled-components';

path.resolve('./next.config.js');
declare global {
  interface Window {
    jQuery: unknown;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    $: any;
    deployedEnvType: string;
    impressionArr: string[];
    impressionTrack: (value: string) => Record<string, string>;
    dataLayer?: Record<string, unknown>;
  }
}

if (process.env.NODE_ENV !== 'test') {
  ModelManager.initializeAsync();
}

const modelClient = new CustomModelClient(process.env.NEXT_PUBLIC_AEM_HOST);

if (process.env.NODE_ENV !== 'test') {
  ModelManager.initializeAsync({
    modelClient,
  });
}

/**
 * returns the lang-selector modal if it's present in top-nav
 * else return's undefined if there is no top-nav authored or if there is not lang selector autored ....
 */
const getLanguageSelectorModalFromTopNavigation = (model: any) => {
  const allItems = model?.cqItems?.root?.[':items']?.responsivegrid?.[':items'];

  // Find the headerResponsivegrid item
  const headerResponsivegrid =
    allItems?.['headerResponsivegrid']?.[':items']?.root?.[':items']?.responsivegrid?.[':items'];

  //added fallback if there is no headerResponsivegrid
  if (!headerResponsivegrid) return undefined;

  // Find the top-navigation item
  const topNavigation: any = Object.values(headerResponsivegrid).find((el: any) =>
    el?.[':type']?.includes('top-navigation')
  );

  //added fallback if there is no top-nav
  if (!topNavigation?.[':items']) return undefined;

  // Find the language-selector inside top-navigation
  return Object.values(topNavigation[':items']).find((el: any) => el?.[':type']?.includes('language-selector'));
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const App = function (props: any) {
  const { Component, pageProps } = props;
  const { model, isAuthorMode, apolloEnvVars, currentLocale, NEXT_GLOBAL_ASSET_PREFIX, featureFlagLogo } = pageProps;
  if (model?.prop_marsha_code) pageProps.serverDataLayer = { prop_marsha_code: model?.prop_marsha_code };
  const respGridModel = respGridUtil(model?.cqItems?.root?.[':items']?.responsivegrid);
  const respGridModelClone = JSON.parse(JSON.stringify(respGridModel)); //To deep clone the model
  const store = useRef<PageModelStoreType>();
  const initialPageModel: PageModelState = {
    pageModel: respGridModel,
  };
  if (!store.current) {
    store.current = createPageModelStore(initialPageModel);
  }
  const setPageModel = useDCAPageModelStore(state => state.setPageModel);
  if (setPageModel) {
    setPageModel(respGridModelClone);
  }
  const allowedComponents = model?.cqItems?.root?.[':items']?.responsivegrid?.allowedComponents?.components;

  const rtlPluginTs = (...args: unknown[]) => {
    return rtlPlugin(args[0] as number, args[1] as string);
  };

  const languageSelectorModal = getLanguageSelectorModalFromTopNavigation(model);

  const envVars = apolloEnvVars ? apolloEnvVars : {};
  envVars['NEXT_PUBLIC_ENABLE_WEBSDK'] =
    getEnvProp('NEXT_PUBLIC_ENABLE_WEBSDK') === 'true' && model?.enableWebSDK === 'true';

  /***
   * Setting the search URL's in store to make them available in client side.
   */
  envVars['NEXT_PUBLIC_SUBMIT_SEARCH_URL'] = pageProps?.NEXT_PUBLIC_SUBMIT_SEARCH_URL;
  envVars['NEXT_PUBLIC_AVAILABILITY_SEARCH_URL'] = pageProps?.NEXT_PUBLIC_AVAILABILITY_SEARCH_URL;
  envVars['NEXT_PUBLIC_APP_NAME'] = pageProps?.NEXT_PUBLIC_APP_NAME;
  envVars['GOOGLE_MAP_API_KEY'] = pageProps?.GOOGLE_MAP_API_KEY;
  envVars['BAIDU_MAP_API_KEY'] = pageProps?.BAIDU_MAP_API_KEY;
  envVars['FEATURE_FLAG_API_KEY_SERVER'] = pageProps?.FEATURE_FLAG_API_KEY_SERVER;
  envVars['ACCEPT_LANGUAGE'] = currentLocale?.replace('_', '-');

  const setEnvVars = useClientEnvVarsStore(state => state.setEnvVars);

  if (setEnvVars) {
    setEnvVars(envVars);
  }

  const isRtl = pageProps?.model?.rtlEnable || pageProps?.rtlEnable || false; // Default to LTR if not provided

  useEffect(() => {
    if (isAuthorMode) {
      let remoteURL: URL | string;
      try {
        remoteURL = new URL(document.body.dataset.remoteUrl || '');
        remoteURL = remoteURL.origin;
      } catch (e) {
        remoteURL = '';
      }
      const assetUrl = `${
        process.env['NEXT_PUBLIC_ASSET_DOMAIN'] || remoteURL || 'https://www.marriott.com'
      }${NEXT_GLOBAL_ASSET_PREFIX}`;

      const globalStyle = document.createElement('link');

      globalStyle.rel = 'stylesheet';
      globalStyle.href =
        assetUrl +
        (isRtl ? `global-styles/${version}/marriot.global.rtl.css` : `global-styles/${version}/marriot.global.css`);
      document.head.appendChild(globalStyle);
    }
  }, []);

  if (canUseDOM) {
    populatePIdataLayer();
  }
  let subDirectoryContextPath = '';

  if (model) {
    subDirectoryContextPath = model?.subDirectoryContextPath ? model?.subDirectoryContextPath : '';
    setSubDirectoryPrefix(subDirectoryContextPath);
  }

  defineComponentMapping(allowedComponents, isAuthorMode, pageProps, languageSelectorModal, featureFlagLogo);
  Object.defineProperty(rtlPluginTs, 'name', { value: 'rtlPluginMonorepo' });
  return (
    // For ACDL setup - @see https://marriottcloud.atlassian.net/browse/DXP-16772
    <UserSessionProvider pageProps={pageProps} includePiData={true}>
      <PageModelContext.Provider value={store.current}>
        <ACDLanalyticsWrapper />
        <StyleSheetManager stylisPlugins={isRtl ? [rtlPluginTs] : []}>
          <Component {...pageProps} />
        </StyleSheetManager>
      </PageModelContext.Provider>
    </UserSessionProvider>
  );
};

export default App;
